// Stock · Transferencias y Reservas (docs/108 B7). Un TRASLADO mueve stock entre ubicaciones (bodega →
// camión, entre bodegas): se arma como borrador y CONFIRMAR es lo que mueve el libro (salida y entrada
// en el mismo lote). RESERVAS: lo que las NV confirmadas todavía no tienen reservado, lo que vence
// pronto y los sobrecompromisos (reservado > físico), todo derivado por el backend.
import { GuiaInternaDe } from '../ventas/GuiaInterna';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { decimal, fecha, fechaHora } from '../comun/formato';
import { cuatroDecimales, DialogoMotivo, hoy } from '../compras/comun';
import { useSesion } from '../sesion/SesionContexto';
import { BuscadorProducto } from '../ventas/BuscadorProducto';
import { useResumenProductos } from './datos';
import { SelectorBodega, useBodegas } from './PantallasStock';

interface Traslado {
  id: string;
  numero: string;
  origen_id: string;
  destino_id: string;
  fecha_negocio: string;
  motivo: string;
  estado: 'BORRADOR' | 'CONFIRMADO' | 'DESCARTADO';
  version: number;
  creado_en: string;
  lineas?: Array<{ id: string; numero: number; producto_id: string; cantidad_base: string }>;
}

function useAccionStock(empresaId: string) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  return useMutation<unknown, Error, { ruta: string; cuerpo: unknown }>({
    mutationFn: ({ ruta, cuerpo }) =>
      api.post(ruta, cuerpo, { idempotencia: intentos.clave(`traslado:${ruta}`, cuerpo) }),
    onSuccess: async () => {
      intentos.cerrar();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'traslados'] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'disponibilidad'] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'stock'] }),
      ]);
    },
    onError: (e) => intentos.cerrar(e),
  });
}

// ================================================================ transferencias

export function Transferencias() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const [estado, setEstado] = useState('');
  const [nuevo, setNuevo] = useState(false);
  const [sel, setSel] = useState<Traslado | null>(null);
  const [descartar, setDescartar] = useState<Traslado | null>(null);
  const [guia, setGuia] = useState<string | null>(null);
  const accion = useAccionStock(empresaId);
  const q = useQuery({
    queryKey: ['emp', empresaId, 'traslados', estado],
    queryFn: () =>
      api.get<{ items: Traslado[] }>('/inventario/traslados', {
        query: { empresaId, estado: estado || undefined },
      }),
  });
  const detalle = useQuery({
    queryKey: ['emp', empresaId, 'traslados', 'detalle', sel?.id],
    queryFn: () => api.get<Traslado>(`/inventario/traslados/${sel!.id}`),
    enabled: !!sel,
  });
  const productos = useResumenProductos(
    empresaId,
    (detalle.data?.lineas ?? []).map((l) => l.producto_id),
  );
  const ubic = (id: string) => {
    const b = bodegas.lista.find((x) => x.id === id);
    return b ? `${b.codigo} · ${b.nombre}` : '';
  };
  const columnas: Columna<Traslado>[] = [
    {
      id: 'n',
      titulo: 'N°',
      celda: (t) => <span className="mono">{`GTI-${t.numero}`}</span>,
      ancho: '7rem',
    },
    { id: 'f', titulo: 'Fecha', celda: (t) => fecha(t.fecha_negocio), ancho: '7rem' },
    { id: 'o', titulo: 'Origen', celda: (t) => ubic(t.origen_id) },
    { id: 'd', titulo: 'Destino', celda: (t) => ubic(t.destino_id) },
    { id: 'm', titulo: 'Motivo', celda: (t) => t.motivo },
    { id: 'e', titulo: 'Estado', celda: (t) => t.estado.toLowerCase(), ancho: '7rem' },
    {
      id: 'acc',
      titulo: '',
      ancho: '13rem',
      celda: (t) =>
        t.estado === 'BORRADOR' ? (
          <span className="acciones-fila">
            {puede('inventario.traslado.confirmar') && (
              <button
                type="button"
                className="boton-mini primario"
                onClick={() =>
                  accion.mutate({
                    ruta: `/inventario/traslados/${t.id}/confirmar`,
                    cuerpo: { version: t.version },
                  })
                }
              >
                Confirmar
              </button>
            )}
            <button type="button" className="boton-mini" onClick={() => setDescartar(t)}>
              Descartar
            </button>
          </span>
        ) : t.estado === 'CONFIRMADO' ? (
          <button type="button" className="boton-mini" onClick={() => setGuia(t.id)}>
            Guía interna
          </button>
        ) : null,
    },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="transferencias">
      {guia && <GuiaInternaDe origen={{ trasladoId: guia }} alTerminar={() => setGuia(null)} />}
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado del traslado"
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="BORRADOR">Borrador</option>
            <option value="CONFIRMADO">Confirmado</option>
            <option value="DESCARTADO">Descartado</option>
          </select>
        </label>
        <span className="espaciador" />
        {puede('inventario.traslado.crear') && (
          <button type="button" className="primario" onClick={() => setNuevo(true)}>
            Nuevo traslado
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      {accion.isError && !descartar && <PanelError error={accion.error} />}
      <Grilla
        etiqueta="Traslados"
        columnas={columnas}
        filas={q.data?.items ?? []}
        clave={(t) => t.id}
        cargando={q.isLoading}
        vacio="Sin traslados"
        alSeleccionar={setSel}
      />
      {sel && detalle.data && (
        <table className="grilla compacta" aria-label="Líneas del traslado">
          <thead>
            <tr>
              <th>N°</th>
              <th>Producto</th>
              <th className="der">Cantidad (unid. base)</th>
            </tr>
          </thead>
          <tbody>
            {(detalle.data.lineas ?? []).map((l) => (
              <tr key={l.id}>
                <td>{l.numero}</td>
                <td>
                  {productos.mapa.get(l.producto_id)
                    ? `${productos.mapa.get(l.producto_id)!.sku} · ${productos.mapa.get(l.producto_id)!.nombre}`
                    : l.producto_id.slice(0, 8)}
                </td>
                <td className="der">{decimal(l.cantidad_base)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {nuevo && <NuevoTraslado alCerrar={() => setNuevo(false)} />}
      {descartar && (
        <DialogoMotivo
          titulo={`Descartar GTI-${descartar.numero}`}
          textoBoton="Descartar"
          pendiente={accion.isPending}
          error={accion.isError ? accion.error : null}
          alConfirmar={(motivo) =>
            accion.mutate(
              {
                ruta: `/inventario/traslados/${descartar.id}/descartar`,
                cuerpo: { version: descartar.version, motivo },
              },
              { onSuccess: () => setDescartar(null) },
            )
          }
          alCerrar={() => setDescartar(null)}
        />
      )}
    </div>
  );
}

function NuevoTraslado({ alCerrar }: { alCerrar: () => void }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const accion = useAccionStock(empresaId);
  const [origen, setOrigen] = useState('');
  const [destino, setDestino] = useState('');
  const [motivo, setMotivo] = useState('');
  const [lineas, setLineas] = useState<
    Array<{ productoId: string; sku: string; nombre: string; cantidad: string }>
  >([]);
  const [buscando, setBuscando] = useState(false);
  const o = origen || bodegas.lista[0]?.id || '';
  const d = destino || bodegas.lista.find((b) => b.id !== o)?.id || '';
  const valido =
    !!o &&
    !!d &&
    o !== d &&
    motivo.trim().length >= 3 &&
    lineas.length > 0 &&
    lineas.every((l) => l.cantidad.trim() && Number(l.cantidad.replace(',', '.')) > 0);
  const crear = (confirmar: boolean) =>
    accion.mutate(
      {
        ruta: '/inventario/traslados',
        cuerpo: {
          empresaId,
          origenId: o,
          destinoId: d,
          fechaNegocio: hoy(),
          motivo: motivo.trim(),
          lineas: lineas.map((l) => ({
            productoId: l.productoId,
            cantidadBase: cuatroDecimales(l.cantidad),
          })),
          confirmar,
        },
      },
      { onSuccess: alCerrar },
    );
  return (
    <div className="panel-revision" role="form" aria-label="Nuevo traslado">
      <div className="barra-filtros">
        <div>
          <span className="etiqueta">Origen</span>
          <SelectorBodega bodegas={bodegas.lista} valor={o} alCambiar={setOrigen} />
        </div>
        <div>
          <span className="etiqueta">Destino</span>
          <SelectorBodega bodegas={bodegas.lista} valor={d} alCambiar={setDestino} />
        </div>
        <label className="campo">
          <span className="etiqueta">Motivo</span>
          <input
            aria-label="Motivo del traslado"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </label>
      </div>
      <table className="grilla compacta" aria-label="Productos a trasladar">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Producto</th>
            <th className="der">Cantidad (unid. base)</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lineas.map((l, i) => (
            <tr key={l.productoId}>
              <td className="mono">{l.sku}</td>
              <td>{l.nombre}</td>
              <td className="der">
                <input
                  className="entrada-numero"
                  aria-label={`Cantidad ${l.sku}`}
                  value={l.cantidad}
                  onChange={(e) =>
                    setLineas((ls) =>
                      ls.map((x, j) => (j === i ? { ...x, cantidad: e.target.value } : x)),
                    )
                  }
                />
              </td>
              <td>
                <button
                  type="button"
                  className="boton-mini"
                  aria-label={`Quitar ${l.sku}`}
                  onClick={() => setLineas((ls) => ls.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {o === d && <p className="tono-malo">Origen y destino deben ser distintos.</p>}
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-modal">
        <button type="button" onClick={() => setBuscando(true)}>
          Agregar producto
        </button>
        <span className="espaciador" />
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button type="button" disabled={!valido || accion.isPending} onClick={() => crear(false)}>
          Guardar borrador
        </button>
        {puede('inventario.traslado.confirmar') && (
          <button
            type="button"
            className="primario"
            disabled={!valido || accion.isPending}
            onClick={() => crear(true)}
          >
            Trasladar ahora
          </button>
        )}
      </div>
      {buscando && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={null}
          ubicacionId={o}
          ubicaciones={bodegas.lista}
          alElegir={(p) => {
            setBuscando(false);
            if (lineas.some((l) => l.productoId === p.producto.id)) return;
            setLineas((ls) => [
              ...ls,
              {
                productoId: p.producto.id,
                sku: p.producto.sku,
                nombre: p.producto.nombre,
                cantidad: String(Number(p.presentacion.factor_a_base)),
              },
            ]);
          }}
          alCerrar={() => setBuscando(false)}
        />
      )}
    </div>
  );
}

// ================================================================ reservas

interface PendienteReserva {
  notaId: string;
  numeroVisible: string;
  canal: string;
  lineaNumero: number;
  productoId: string;
  requerida: string;
  reservada: string;
  pendiente: string;
}
interface ReservaVigente {
  reserva_id: string;
  ubicacion_id: string;
  producto_id: string;
  cantidad_vigente: string;
  expira_en: string | null;
  origen_id?: string;
}
interface FilaDisp {
  ubicacionId: string;
  productoId: string;
  fisico: string;
  reservado: string;
  disponible: string;
}

export function Reservas({
  alAbrirNota,
}: {
  alAbrirNota: (notaId: string, numero: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const verInventario = puede('inventario.ver');
  const pendientes = useQuery({
    queryKey: ['emp', empresaId, 'reservas', 'pendientes'],
    queryFn: () =>
      api.get<{ items: PendienteReserva[] }>('/ventas/reservas/pendientes', {
        query: { empresaId },
      }),
  });
  const porVencer = useQuery({
    queryKey: ['emp', empresaId, 'reservas', 'por-expirar'],
    queryFn: () =>
      api.get<ReservaVigente[]>('/inventario/reservas/por-expirar', {
        query: { empresaId, horas: 48 },
      }),
    enabled: verInventario,
  });
  const sobre = useQuery({
    queryKey: ['emp', empresaId, 'reservas', 'sobrecompromisos'],
    queryFn: () =>
      api.get<FilaDisp[]>('/inventario/reservas/sobrecompromisos', { query: { empresaId } }),
    enabled: verInventario,
  });
  const ids = [
    ...(pendientes.data?.items ?? []).map((p) => p.productoId),
    ...(porVencer.data ?? []).map((r) => r.producto_id),
    ...(sobre.data ?? []).map((s) => s.productoId),
  ];
  const productos = useResumenProductos(empresaId, ids);
  const prod = (id: string) => {
    const p = productos.mapa.get(id);
    return p ? `${p.sku} · ${p.nombre}` : id.slice(0, 8);
  };
  const ubic = (id: string) => bodegas.lista.find((b) => b.id === id)?.nombre ?? '';
  return (
    <div className="pantalla consulta" data-pantalla="reservas">
      <h3 className="titulo-seccion">NV confirmadas con unidades sin reservar</h3>
      {pendientes.isError && <PanelError error={pendientes.error} />}
      <Grilla
        etiqueta="Pendientes de reservar"
        columnas={[
          {
            id: 'n',
            titulo: 'Nota',
            celda: (p) => <span className="mono">{p.numeroVisible}</span>,
            ancho: '8rem',
          },
          { id: 'l', titulo: 'Línea', celda: (p) => p.lineaNumero, ancho: '4rem' },
          { id: 'p', titulo: 'Producto', celda: (p) => prod(p.productoId) },
          {
            id: 'r',
            titulo: 'Requerida',
            celda: (p) => decimal(p.requerida),
            alinear: 'der',
            ancho: '7rem',
          },
          {
            id: 'res',
            titulo: 'Reservada',
            celda: (p) => decimal(p.reservada),
            alinear: 'der',
            ancho: '7rem',
          },
          {
            id: 'pe',
            titulo: 'Sin reservar',
            celda: (p) => <b className="tono-malo">{decimal(p.pendiente)}</b>,
            alinear: 'der',
            ancho: '8rem',
          },
        ]}
        filas={pendientes.data?.items ?? []}
        clave={(p) => `${p.notaId}:${p.lineaNumero}`}
        cargando={pendientes.isLoading}
        vacio="Todas las NV confirmadas tienen su reserva"
        alAbrir={(p) => alAbrirNota(p.notaId, p.numeroVisible)}
      />
      {verInventario && (
        <>
          <h3 className="titulo-seccion">Reservas que vencen en las próximas 48 horas</h3>
          <Grilla
            etiqueta="Reservas por vencer"
            columnas={[
              { id: 'p', titulo: 'Producto', celda: (r) => prod(r.producto_id) },
              { id: 'u', titulo: 'Ubicación', celda: (r) => ubic(r.ubicacion_id), ancho: '12rem' },
              {
                id: 'c',
                titulo: 'Cantidad',
                celda: (r) => decimal(r.cantidad_vigente),
                alinear: 'der',
                ancho: '7rem',
              },
              { id: 'v', titulo: 'Vence', celda: (r) => fechaHora(r.expira_en), ancho: '10rem' },
            ]}
            filas={porVencer.data ?? []}
            clave={(r) => r.reserva_id}
            cargando={porVencer.isLoading}
            vacio="Ninguna reserva vence pronto"
          />
          <h3 className="titulo-seccion">Sobrecompromisos (reservado mayor que el físico)</h3>
          <Grilla
            etiqueta="Sobrecompromisos"
            columnas={[
              { id: 'p', titulo: 'Producto', celda: (s) => prod(s.productoId) },
              { id: 'u', titulo: 'Ubicación', celda: (s) => ubic(s.ubicacionId), ancho: '12rem' },
              {
                id: 'f',
                titulo: 'Físico',
                celda: (s) => decimal(s.fisico),
                alinear: 'der',
                ancho: '7rem',
              },
              {
                id: 'r',
                titulo: 'Reservado',
                celda: (s) => decimal(s.reservado),
                alinear: 'der',
                ancho: '7rem',
              },
              {
                id: 'd',
                titulo: 'Disponible',
                celda: (s) => <b className="tono-malo">{decimal(s.disponible)}</b>,
                alinear: 'der',
                ancho: '7rem',
              },
            ]}
            filas={sobre.data ?? []}
            clave={(s) => `${s.ubicacionId}:${s.productoId}`}
            cargando={sobre.isLoading}
            vacio="Sin sobrecompromisos"
          />
        </>
      )}
    </div>
  );
}
